import { getRecoil } from "recoil-nexus";

import mainStore, { storeTriggerdBetId, storeSetShowSuccessState, storeSetSuperbet } from "src/stores/main";
import { ISwarmDoBetTerminalResponse } from "src/common/swarm/models/response";
import { ISwarmDoBetTerminalRequest } from "src/common/swarm/models/request";
import { emitBetPlaced } from "../communicator/actions";
import upperDataStore, { storeGetConfig } from "src/stores/upperData";
import { IPlaceBetRequestArg } from "src/constants/interfaces/bet";
import { toastWrapper } from "src/common/components/toastWrapper";
import { PriceChangeEnum } from "src/constants/enums/priceChange";
import { getPrice } from "src/rules/calculation/priceCalculation";
import { storeChangeSelection } from "src/stores/selections";
import TerminalSwarmActions from "../swarmActions/terminal";
import { BetTypeEnum } from "src/constants/enums/betType";
import { viewMessage } from "src/common/helpers/view";
import { numericBoolean } from "src/constants/types";
import { errorCodes } from "src/constants/errorCodes";
import { hasProfitBoost } from "src/rules/profitBoost";
import { resolveBetType } from "src/common/helpers/betBuilder";
import { refreshBetBuilderOddsKeepingRejected } from "src/services/betBuilderRefresh";
import betBuilderStore from "src/stores/betBuilder";
import { ISelection } from "src/constants/interfaces/bet";

class TerminalPlaceBet {
  /**
   * Map a selection to its server-side bet entry. Bet builder selections carry
   * a `bet_builder_match_id` (the game id) so the server can group them.
   *
   * For BB legs we send the CARD'S COMBINED ODD as the per-leg price (every
   * leg of the same card carries the same price value — that combined odd
   * from `get_bet_builder_odds`). The server validates against that combined
   * value rather than per-leg odds, so sending the leg's individual price
   * triggers "odds have been changed" errors the user can't recover from.
   * Falls back to the leg's own price when the BB store hasn't been
   * populated yet (defensive — should never happen in practice because the
   * place-bet button is disabled while BB odds are loading).
   */
  private static toBetEntry = (
    selection: ISelection,
    betType: BetTypeEnum,
    betBuilderGroups: Record<number, { combinedPrice: number }>,
  ): ISwarmDoBetTerminalRequest["bets"][number] => {
    const isBb = !!selection.event.isInternalBetBuilder;
    const combined = isBb
      ? betBuilderGroups[selection.game.id as number]?.combinedPrice
      : undefined;

    return {
      event_id: selection.event.id,
      price: combined && combined > 0 ? combined : getPrice(selection),
      is_banker: betType === BetTypeEnum.System ? !!selection.isBanker : null,
      ...(isBb ? { bet_builder_match_id: selection.game.id } : {}),
    };
  };

  private static createBetRequest = ({ selections, amount, BTag }: IPlaceBetRequestArg) => {
    const {
      betType,
      isSuperbet,
      isEachWay,
      priceChangeType,
      systemCombinationCount,
      profitBoostPercent,
      freeBets,
      isProfitBoost,
    } = getRecoil(mainStore);

    const { userId, memberId, verificationResponse } = getRecoil(upperDataStore);
    // Combined odd per BB game id — sourced from the last get_bet_builder_odds
    // response. `toBetEntry` reads it for every BB leg so they all carry the
    // matching combined value.
    const { groups: betBuilderGroups } = getRecoil(betBuilderStore);
    const config = storeGetConfig();
    const isCounterOffer = selections.some(item => item.priceChangeType === PriceChangeEnum.CounterOffer);

    const request: ISwarmDoBetTerminalRequest = {
      type: resolveBetType(selections, betType),
      mode: isCounterOffer ? PriceChangeEnum.CounterOffer : priceChangeType,
      is_offer: +isCounterOffer as numericBoolean,
      is_super_bet: isSuperbet || isCounterOffer || config.superbetHiddenMode,
      each_way: isEachWay,
      member_id: verificationResponse?.data?.id || memberId,
      amount: isEachWay ? amount * 2 : amount,
      boost_percentage: isProfitBoost ? profitBoostPercent : null,
      profit_boost_percent: profitBoostPercent,
      BTag,
      bets: selections.map(item => TerminalPlaceBet.toBetEntry(item, betType, betBuilderGroups)),
    };

    const selectedFreeBet = freeBets.find((freeBet) => freeBet.selected);

    if (selectedFreeBet) {
      request.amount = selectedFreeBet.details.amount;
      request.client_bonus_id = selectedFreeBet.details.id;
    }

    if (betType === BetTypeEnum.System && systemCombinationCount) request.sys_bet = systemCombinationCount;

    if (userId) {
      request.client_id = userId;
      request.is_player_betting = true;
    }

    return request;
  };

  private static makeBetRequest = (data: IPlaceBetRequestArg) => {
    const request = TerminalPlaceBet.createBetRequest(data);
    return TerminalSwarmActions.placeBet(request);
  };

  private static processRequestSingleList = ({ selections, amount, ...rest }: IPlaceBetRequestArg) => {
    const config = storeGetConfig();

    const requests = selections.map(bet => {
      const stake = config.enableSingleStakeInput ? bet.singleStake || 0 : amount;
      return TerminalPlaceBet.makeBetRequest({ selections: [bet], amount: +stake, ...rest });
    });

    return requests;
  };

  private static processRequest = (data: IPlaceBetRequestArg) => {
    const config = storeGetConfig();
    const { betType } = getRecoil(mainStore);

    if (config.enableSingles && betType === BetTypeEnum.Single)
      return TerminalPlaceBet.processRequestSingleList(data);

    return [TerminalPlaceBet.makeBetRequest(data)];
  };

  private static processResponse = (
    succeedBets: ISwarmDoBetTerminalResponse[],
    rejectedBets: ISwarmDoBetTerminalResponse[],
    timeout?: boolean,
  ) => {
    const mainState = getRecoil(mainStore);
    const config = storeGetConfig();

    if (timeout) {
      toastWrapper('error', 'Place bet timeout, please retry!');
      return [];
    }

    const noSuperbets = succeedBets.every(item => !item.is_super_bet);

    !rejectedBets.length && noSuperbets && config.enableSuccessState && storeSetShowSuccessState(true);

    if (noSuperbets && mainState.isSuperbet) storeSetSuperbet(false);

    const succeedSelectionIds = succeedBets.reduce<number[]>(
      (acc, item) => [...acc, ...item.selection_list.map(({ selection_id }) => selection_id)],
      []
    );

    return succeedSelectionIds;
  };

  public static placeBet = async (data: IPlaceBetRequestArg) => new Promise(async (resolve) => {
    const { selections, exactLosing } = data;
    const upperDataState = getRecoil(upperDataStore);
    const { betType, profitBoostPercent, isProfitBoost, isSuperbet } = getRecoil(mainStore);
    const config = storeGetConfig();

    const isCounterOffer = selections.some(item => item.priceChangeType === PriceChangeEnum.CounterOffer);
    const isAvailableSuperBet = isSuperbet || isCounterOffer;
    const boostPercentage = isProfitBoost ? profitBoostPercent : null;

    if (config.verification.enabled && !upperDataState.verificationResponse?.data?.id && !upperDataState.memberId) {
      toastWrapper("error", `Error processing the request, Something went wrong while getting player information`);
      upperDataState.verificationResolver?.logout()
      resolve(TerminalPlaceBet.processResponse([], []));

      return;
    }

    const placeBetTimeout = setTimeout(() => {
      resolve(TerminalPlaceBet.processResponse([], [], true))
    }, storeGetConfig().placeBetTimeoutSeconds * 1000);

    const responses = await Promise.allSettled(TerminalPlaceBet.processRequest(data));

    const succeedBets = (
      responses.filter(
        ({ status }) => status === "fulfilled"
      ) as PromiseFulfilledResult<ISwarmDoBetTerminalResponse>[]
    ).map(({ value }) => ({ ...value, exactLosing: !!exactLosing }));

    if (hasProfitBoost(config, upperDataState.partnerConfig, selections, betType, data.amount) && !boostPercentage && !isAvailableSuperBet && !profitBoostPercent) {
      const lastBetId = succeedBets.at(-1)?.id;
      if (typeof lastBetId === "number") storeTriggerdBetId(lastBetId);
    }

    const rejectedBets = (
      responses.filter(({ status }) => status === "rejected") as PromiseRejectedResult[]
    ).map(({ reason }) => reason);

    succeedBets.forEach(bet => emitBetPlaced({
      data: bet,
      verification: upperDataState.verificationResponse,
      stakeAmount: data.amount
    }));

    // Track whether any rejection touched a bet-builder leg — if so we
    // re-run get_bet_builder_odds after this loop to refresh combined odds
    // and mark any newly-rejected legs as inactive in the slip.
    let rejectionAffectsBetBuilder = false;

    rejectedBets.forEach(({ details, result, errorCode }) => {
      // TODO: proper implementation of errors message

      if (!details) return toastWrapper("error", `Error processing the request ${errorCode || ''}`.trim());

      const selectionId = +details.selection_id;
      const actualPrice = details.actual_price;
      const selectionWithError = selections.find(item => item.event.id === selectionId);
      const resultCode = result.toString();

      if (selectionWithError) {
        if (selectionWithError.event.isInternalBetBuilder) {
          rejectionAffectsBetBuilder = true;
        }
        storeChangeSelection(
          selectionId,
          {
            ...selectionWithError,
            hasError: true,
            event: {
              ...selectionWithError.event,
              price: +actualPrice || selectionWithError.event.price
            },
          }
        );
      }

      toastWrapper("error", errorCodes[resultCode] || details.error_message);
      viewMessage.error(details.error_message, "");
      console.log(details, "placeBet: got error");
    });

    // "Odds have been changed" (or any other rejection landing on a BB leg)
    // invalidates the cached combined odd. Re-fetch via the imperative
    // refresh path so RejectedIds get applied as `inactive: true` instead
    // of removed — the user sees them flagged as deleted inside the BB
    // card and the standard notify + place-bet-block rules take over.
    if (rejectionAffectsBetBuilder) {
      refreshBetBuilderOddsKeepingRejected();
    }

    clearTimeout(placeBetTimeout);

    resolve(TerminalPlaceBet.processResponse(succeedBets, rejectedBets));
  });
}

export default TerminalPlaceBet;
